import { MipsDataService } from './mips-data-service';

export interface DecileRange { from: number; to: number }

// Only use real benchmark data - no fallback to mock data
export function getDecilesForMeasure(id: string, inverse = false): DecileRange[] {
  try {
    const dataService = MipsDataService.getInstance();
    const deciles = dataService.getDecilesForMeasure(id);
    
    // Only return data if measure is active and has benchmarks
    if (deciles.length > 0) {
      return deciles.map(d => ({ from: d.from, to: d.to }));
    }
  } catch (error) {
    console.error(`Failed to load benchmark data for measure ${id}:`, error);
  }
  
  // Return empty array if no data - no fallback to mock
  return [];
}

// Method-aware deciles retrieval for Option B (per-method tracking)
export function getDecilesForMeasureBySubmissionMethod(measureId: string, submissionMethod: string): DecileRange[] {
  try {
    const dataService = MipsDataService.getInstance();
    const measureData = dataService.getMeasureWithSubmissionMethod(measureId, submissionMethod);
    if (measureData && Array.isArray(measureData.deciles) && measureData.deciles.length > 0) {
      return measureData.deciles.map(d => ({ from: d.from, to: d.to }));
    }
  } catch (error) {
    console.error(`Failed to load benchmark data for measure ${measureId} (${submissionMethod}):`, error);
  }
  return [];
}

// Generate mock decile data (original implementation)
function generateMockDeciles(id: string, inverse = false): DecileRange[] {
  function hashId(id: string) {
    let h = 0;
    for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
    return h;
  }

  const h = hashId(id);
  const points = [0];
  for (let i = 1; i < 10; i++) {
    const base = i * 10;
    const jitter = ((h >> (i % 16)) & 0xf) - 8; // -8..+7
    const adjusted = Math.min(100, Math.max(0, base + jitter * 0.6));
    points.push(adjusted);
  }
  points.push(100);
  // Ensure sorted/monotonic
  points.sort((a, b) => a - b);
  for (let i = 1; i < points.length; i++) {
    if (points[i] <= points[i - 1]) points[i] = Math.min(100, points[i - 1] + 0.1);
  }
  const ranges: DecileRange[] = [];
  for (let i = 0; i < 10; i++) {
    ranges.push({ from: points[i], to: points[i + 1] });
  }
  // We keep numeric scale 0..100 left->right regardless; inverse affects semantics only.
  // Could flip highlighting logic if desired; for now we just label it in the UI.
  return ranges;
}
